import {
  Combobox,
  DropdownButton,
  NoOptionsPlaceholder
} from '@eggplant/pattern-library'
import { FiPlus } from 'react-icons/fi'
import { GetModelSuiteMonitorConfigsRequest } from '@eggplant/api-requests'
import { HiOutlineDocumentText } from 'react-icons/hi'
import {
  LinkToAddTestConfig,
  useSpaceAccess
} from '@eggplant/routing'
import { TEST_CONFIG } from './constants'
import type {
  ButtonShape,
  GetFilterIdListResponse,
  GetModelSuiteMonitorConfigsRequestParams,
  Options
} from '@eggplant/types'

interface Props {
  onChange: (value: string) => void
  placeholder?: string
  value?: string
  shortErrorMessage?: string
  isHighlightedDropdown?: boolean
}

const getOptions = (items: GetFilterIdListResponse[] | undefined) => {
  if (!Array.isArray(items)) return []

  return items.map(item => ({
    label: item.name,
    value: item.id_list.join(',')
  }))
}

const SelectModelSuiteMonitorConfigs = ({
  onChange,
  placeholder = 'Monitor config',
  value,
  shortErrorMessage,
  isHighlightedDropdown
}: Props) => {
  const [isAccessible] = useSpaceAccess()

  const params = { type: 'monitor' } as GetModelSuiteMonitorConfigsRequestParams

  // eslint-disable-next-line complexity
  const renderButton = (options: Options) => {
    const currentOption = value
      ? options.find(item => item.value.includes(value))
      : undefined

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const testconfigTooltip = [TEST_CONFIG].includes(String(value))
      ? label
      : `Monitor config: ${label}`

    const tooltip = currentOption?.value
      ? testconfigTooltip
      : placeholder

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        Icon={HiOutlineDocumentText}
        isFilter
        isHighlighted={
          isHighlightedDropdown
            ? !!value
            : false
        }
        label={label}
        title={tooltip}
      >
        {buttonText}
      </DropdownButton>
  }

  const noDataPlaceholder =
    <NoOptionsPlaceholder
      icon={<HiOutlineDocumentText size='24px' />}
      linkTo={
        isAccessible
          ? (
            <LinkToAddTestConfig>
              <FiPlus
                aria-hidden
                size={16}
              />
              New Monitor Config
            </LinkToAddTestConfig>
          )
          : undefined
      }
      title='No monitor config found'
    />

  return (
    <div className='flex'>
      <GetModelSuiteMonitorConfigsRequest
        params={params}
        shortErrorMessage={shortErrorMessage || 'Failed to load Monitor config'}
      >
        {
          (data: GetFilterIdListResponse[]) =>
            <Combobox
              isFilter
              noOptionsPlaceholder={noDataPlaceholder}
              onChange={onChange}
              options={getOptions(data)}
              renderButton={renderButton(getOptions(data))}
              value={value}
            />
        }
      </GetModelSuiteMonitorConfigsRequest>
    </div>
  )
}

export default SelectModelSuiteMonitorConfigs
